import React from 'react'
import './notification-details.scss'
import { observer } from 'mobx-react'
import { observe } from 'mobx'
import * as moment from 'moment-timezone'
import * as numeral from 'numeral'
import {
    AndroidFilled,
    AppleFilled,
    ChromeFilled,
    ClockCircleFilled,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    RetweetOutlined,
    TrophyFilled,
} from '@ant-design/icons'
import { Button, Col, Divider, Popover, Row, Select, Skeleton, Tag, Tooltip } from 'antd'
import { AppService, NotificationService } from '../../../services'
import { AppState } from '../../../stores/app'
import { Container } from 'typescript-ioc/es5'
import { DomainDto } from '../../../dtos/domain'
import { StatusType } from '../../../enums/status-type'
import {
    arrayContains,
    backfillStats,
    numberWithCommas,
    numberWithPercent,
    preventBubbling,
    titleCase,
} from '../../../_utils/utils'
import {
    BASE_TIME_FORMAT,
    BASE_TIME_FORMAT_WITHOUT_TZ,
    DEFAULT_HIGHCHARTS_CONFIG,
    HIGHCHARTS_CHANNEL_COLOR_MAP,
} from '../../../constants'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { SegmentDto } from '../../../dtos/segment'
import { NotificationSummaryCard, PageHeader, PushlyAi, Well } from '@pushly/aqe/lib/components'
import {
    DetailsSummaryWell,
    DetailsSummarySection,
    DetailsSummarySubSections,
    DetailsSummaryField,
} from '@pushly/aqe/lib/components/details-summary-well'
import { BaseNotificationDetails, IBaseNotificationDetailsState } from './base-notification-details-component'
import { NotificationDto } from '../'
import { InsightsService } from '../../../services/insights'

import { NotificationDeliveryWindow } from '../../../enums/notification-delivery-window'
import { NotificationDeliveryType } from '../../../enums/notification-delivery-type'
import { extractSummaryNotification } from '../../../_utils/notifications'
import { ApiVersion } from '../../../enums/api-version.enum'
import { NoTranslate } from '../../../components/no-translate/no-translate'
import { NotificationStatusBadge } from '../../../components/badges/notification-status-badge'
import { HighchartsUtils } from '../../../_utils/highcharts-utils'
import { onResponseError403 } from '../../../_utils/on-response-error-403'
import { reduceStatTotalsByChannel } from './helpers'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import { NotificationSource } from '../../../enums/notification-source'
import { NotificationPreviewState } from '../../../stores/notification-preview-state'
import { coalesce } from '../../../_utils/coalesce'
import { getDomainCurrencySymbol } from '../../../_utils/domain'
import { ReportEntity } from '../../reporting_v2/enums/report-entity'
import { ReportDatePreset } from '../../reporting_v2/enums/report-date-preset'
import { ReportDateIncrement } from '../../reporting_v2/enums/report-date-increment'
import { InsightsReportContract } from '../../reporting_v2/helpers/build-api-report-contract'
import { PreviewPlatform } from '@pushly/models/lib/enums/preview-platform'
import { PreviewState } from '@pushly/models/lib/enums/preview-state'

Highcharts.setOptions({ lang: { thousandsSep: ',' } })

type ChartType = 'impressions' | 'clicks'

interface IHeaderProps {
    domain: DomainDto
    notification?: NotificationDto
}

class NotificationDetailsHeader extends React.Component<IHeaderProps, {}> {
    protected appState: AppState
    protected appService: AppService

    private unmounting: boolean = false

    public constructor(props: IHeaderProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public componentWillUnmount() {
        this.unmounting = true
    }

    public render(): React.ReactNode {
        const loading = !this.notif
        const isCampaign = this.notif?.source === NotificationSource.CAMPAIGN

        return (
            <div className="details-header">
                <div className="notif-title">
                    <PageHeader
                        title={<NoTranslate>{this.notif?.defaultTemplate.title}</NoTranslate>}
                        append={
                            <div className="notif-id">
                                <span>
                                    #{this.notif?.id}
                                    <span>
                                        {' '}
                                        | Created by <NoTranslate>{this.notif?.createdByUserName}</NoTranslate>&nbsp;
                                    </span>
                                </span>
                            </div>
                        }
                        onTitleSet={this.appService.customizeTabTitle}
                    />
                </div>

                <DetailsSummaryWell loading={loading}>
                    <DetailsSummarySection title="General">
                        <DetailsSummaryField label="ID" value={this.notif?.id} copyable />
                        <DetailsSummaryField
                            label="Status"
                            value={
                                this.notif && (
                                    <NotificationStatusBadge
                                        status={this.notif.status}
                                        expanded={true}
                                        holdOut={
                                            this.notif.deliverySpec?.window === NotificationDeliveryWindow.HOLD_OUT
                                        }
                                    />
                                )
                            }
                        />
                        <DetailsSummaryField label="Channels" value={this.renderChannelValues()} />
                        {this.renderNotificationTestField()}
                    </DetailsSummarySection>

                    <DetailsSummarySubSections>
                        <DetailsSummarySection title="Delivery">
                            {!isCampaign && (
                                <DetailsSummaryField label="Delivery Date Range" value={this.buildFlightDatesValue()} />
                            )}
                            <DetailsSummaryField label="Delivery Strategy" value={this.buildStrategyValue()} />
                            {this.renderBehaviorsField()}
                        </DetailsSummarySection>

                        {!isCampaign && (
                            <DetailsSummarySection title="Targeting">
                                {this.renderSegmentFields()}
                            </DetailsSummarySection>
                        )}
                    </DetailsSummarySubSections>

                    {this.renderContentSection()}
                </DetailsSummaryWell>
            </div>
        )
    }

    protected get notif(): NotificationDto {
        return this.props.notification!
    }

    protected buildFlightDatesValue(): React.ReactNode {
        const isSTZ = this.notif.isTzDelivery()
        const sendDateFormat = isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT

        const startDate = moment.tz(this.notif.deliverySpec.sendDateUtc, this.props.domain.timezone)
        const expiryDate = startDate.clone().add(this.notif.deliverySpec.ttlSeconds, 'seconds')

        const flightDateStart = startDate.format(sendDateFormat)
        const flightDateEnd = expiryDate.format(sendDateFormat)

        return (
            <>
                {flightDateStart} <span style={{ opacity: 0.5 }}>through</span>
                <br />
                {flightDateEnd}
            </>
        )
    }

    protected buildStrategyValue(): string {
        const type = this.notif.deliverySpec.type
        const window = this.notif.deliverySpec.window
        let strategyStr = ''

        if (this.notif.source === NotificationSource.CAMPAIGN) {
            strategyStr = 'campaign schedule'
        } else if (window === NotificationDeliveryWindow.HOLD_OUT) {
            strategyStr = 'hold out'
        } else if (window === NotificationDeliveryWindow.INFORMED) {
            strategyStr = 'informed'
        } else if (type === NotificationDeliveryType.IMMEDIATE) {
            strategyStr = 'immediate'
        } else if (window === NotificationDeliveryWindow.STANDARD) {
            strategyStr = 'fixed'
        } else if (window === NotificationDeliveryWindow.TIMEZONE) {
            strategyStr = 'subscriber time zone'
        }

        return titleCase(strategyStr)
    }

    protected renderContentSection(): React.ReactNode {
        const aiResults = this.notif?.defaultTemplate.autoSuggestedFieldsResults

        // Backward compatibility: Legacy notifications created before the source field was added
        // may have AI-generated content (title, body, imageUrl, keywords) but no source field.
        // In this case, default to 'page_analyzer' since it was the only AI tool available at that time.
        let aiSource = aiResults?.source
        if (
            aiResults &&
            !aiSource &&
            (aiResults.title || aiResults.body || aiResults.imageUrl || aiResults.keywords?.length)
        ) {
            aiSource = 'page_analyzer'
        }

        if (!aiSource) return null

        const isPushlyAi = aiSource === 'pushly_ai'
        const tone = aiResults?.sourceMetadata?.tone ?? aiResults?.tone
        const reason = aiResults?.sourceMetadata?.reason ?? aiResults?.reason

        return (
            <DetailsSummarySection title="Content">
                <DetailsSummaryField
                    label="Content Source"
                    value={
                        <>
                            {isPushlyAi && <PushlyAi />} {isPushlyAi ? 'Pushly AI' : 'Page Analyzer'}
                        </>
                    }
                />
                {tone && <DetailsSummaryField label="AI Content Tone" value={<Tag>{titleCase(tone)}</Tag>} />}
                {reason && <DetailsSummaryField label="AI Content Reason" value={reason} />}
            </DetailsSummarySection>
        )
    }

    protected renderSegmentFields(): React.ReactNode {
        let subscriberTags: React.ReactNode
        let excludedSubscribers: React.ReactNode
        const allSubscriberTag: React.ReactNode = (
            <a key="all-subs" className="no-click">
                <Tag>All Subscribers</Tag>
            </a>
        )
        if (this.notif.audience.segments) {
            let tags: React.ReactNode[] = [allSubscriberTag]
            const isAllSubscribers = this.notif.audience.segments.find((s: SegmentDto) => s.isDefault)

            if (!isAllSubscribers) {
                tags = this.notif.audience.segments
                    .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
                    .map((segment: any, idx: number) => (
                        <a
                            key={`i-${idx}`}
                            onClick={this.jumpToSegment.bind(this, segment)}
                            href={this.buildSegmentUrl(segment)}
                        >
                            <Tag>{segment.name}</Tag>
                        </a>
                    ))
            }

            subscriberTags = <>{tags}</>
        } else if (this.notif.audience.externalSubscriberIds) {
            subscriberTags = <Tag>Subscriber IDs</Tag>
        }

        if (this.notif.audience.excludedSegments && this.notif.audience.excludedSegments.length > 0) {
            excludedSubscribers = (
                <>
                    {this.notif.audience.excludedSegments
                        .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
                        .map((segment: any, idx: number) => (
                            <a
                                key={`x-${idx}`}
                                onClick={this.jumpToSegment.bind(this, segment)}
                                href={this.buildSegmentUrl(segment)}
                            >
                                <Tag>{segment.name}</Tag>
                            </a>
                        ))}
                </>
            )
        }

        return (
            <>
                <DetailsSummaryField label="Targeted Audience(s)" value={subscriberTags} />
                {!!excludedSubscribers && (
                    <DetailsSummaryField label="Excluded Audience(s)" value={excludedSubscribers} />
                )}
            </>
        )
    }

    protected renderChannelValues(): React.ReactNode {
        const channels = this.notif?.channels ?? this.notif?.defaultTemplate.channels
        if (!channels) return undefined

        const items: React.ReactNode[] = []
        if (channels.includes(DeliveryChannel.WEB))
            items.push(
                <span key="web">
                    <ChromeFilled /> Web
                </span>,
            )
        if (channels.includes(DeliveryChannel.NATIVE_IOS))
            items.push(
                <span key="ios">
                    <AppleFilled /> Native Apple
                </span>,
            )
        if (channels.includes(DeliveryChannel.NATIVE_ANDROID))
            items.push(
                <span key="android">
                    <AndroidFilled /> Native Android
                </span>,
            )

        if (items.length === 0) return undefined

        return items.reduce<React.ReactNode[]>((acc, item, i) => {
            if (i > 0) acc.push(', ')
            acc.push(item)
            return acc
        }, [])
    }

    protected renderNotificationTestField(): React.ReactNode {
        if (!this.notif?.abTest) return null

        const testUrl = this.buildAbTestUrl(this.notif)
        const isWinner = !!this.notif.abTest.winner

        return (
            <DetailsSummaryField
                label="Notification Test"
                value={
                    <>
                        <a href={testUrl}>{this.notif.abTest.name}</a>
                        {isWinner && (
                            <>
                                {' '}
                                <TrophyFilled className="test-winner-icon" /> Winner
                            </>
                        )}
                    </>
                }
            />
        )
    }

    protected renderBehaviorsField(): React.ReactNode {
        const behaviors: string[] = []

        if (this.notif?.defaultTemplate.isSilent) {
            behaviors.push('Silent Delivery')
        }
        if (this.notif?.defaultTemplate.requireInteraction) {
            behaviors.push('Requires Interaction')
        }

        if (behaviors.length === 0) return null

        return <DetailsSummaryField label="Behaviors" value={behaviors.join(', ')} />
    }

    private buildSegmentUrl(segment: any): string {
        return this.appService.routeWithinDomain(`/segments/${segment.id}/summary`, true)
    }

    private buildAbTestUrl(notif: any): string {
        return this.appService.routeWithinDomain(`/notifications/test/${notif.abTest.id}/summary`, true)
    }

    private jumpToSegment = (segment: any, ev?: any): void => {
        if (!ev.metaKey) {
            preventBubbling(ev)
            this.appService.route(this.buildSegmentUrl(segment))
        }
    }
}

interface IProps {}

interface IState extends IBaseNotificationDetailsState {
    loadingSchedule: boolean
    loadingGenStats: boolean
    loadingBreakdownStats: boolean
    loadingActionStats: boolean
    domain: DomainDto
    notification?: NotificationDto
    stats?: any
    actionStats?: any[]
    breakdownStats?: any
    statsType: ReportDateIncrement.DAY | ReportDateIncrement.LIFETIME
    performanceMetric: ChartType
    performanceChannel: DeliveryChannel[]
    platform: PreviewPlatform
}

@observer
export class NotificationDetails extends BaseNotificationDetails<IProps, IState> {
    protected appService: AppService
    protected readonly notificationPreviewState: NotificationPreviewState
    private appState: AppState
    private notificationService: NotificationService
    private insightsService: InsightsService

    private disposeObservers: any[]

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.notificationService = Container.get(NotificationService)
        this.insightsService = Container.get(InsightsService)
        this.notificationPreviewState = Container.get(NotificationPreviewState)

        this.state = {
            loadingSchedule: true,
            loadingGenStats: true,
            loadingBreakdownStats: true,
            loadingActionStats: true,
            domain: this.appState.currentDomain!,
            statsType: ReportDateIncrement.DAY,
            tabs: [],
            performanceMetric: 'impressions',
            performanceChannel: [],
            platform: PreviewPlatform.ANDROID,
        }
    }

    public async componentDidMount() {
        this.disposeObservers = [observe(this.appState, 'currentDomainJsonData', () => this.setDomainState())]

        await this.setDomainState()

        const tabs = [
            {
                name: 'summary',
                label: 'Summary',
                render: this.renderSummaryTab,
            },
        ]

        const activeTab = this.determineActiveTab(tabs)
        if (!this.unmounting) {
            await this.setState({
                tabs,
                activeTab,
            })
        }
    }

    public async componentDidUpdate(prevProps, prevState, _) {
        if (prevState.statsType !== this.state.statsType) {
            await this.refreshStats()
        }
    }

    public componentWillUnmount() {
        super.componentWillUnmount()
        this.disposeObservers.forEach((f: any) => f())
    }

    public render(): React.ReactNode {
        let optionsWrapper: React.ReactNode
        let subscribers: React.ReactNode
        let excludedSubscribers: React.ReactNode
        const allSubscriberTag: React.ReactNode = (
            <a key="all-subs">
                <Tag color="gray">All Subscribers</Tag>
            </a>
        )

        let dateAppend: React.ReactNode = ''

        if (this.state.notification) {
            const sample = this.state.notification
            const sampleAudience = sample.audience

            const now = moment()
            const sendDate = moment.tz(sample.deliverySpec.sendDateUtc, this.localDomain!.timezone)
            sendDate.add(sample.deliverySpec.ttlSeconds, 'seconds')

            const isSTZ = sample.isTzDelivery()
            const sendDateFormat = isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT

            dateAppend = (
                <span key="expiry">
                    {sendDate > now ? ' | Expires ' : ' | Expired '} {'on ' + sendDate.format(sendDateFormat)}
                </span>
            )

            if (sample.source !== NotificationSource.CAMPAIGN) {
                if (sampleAudience.segments) {
                    let subscriberTags: React.ReactNode[] = [allSubscriberTag]
                    const isAllSubscribers = sampleAudience.segments.find((s: SegmentDto) => s.isDefault)

                    if (!isAllSubscribers) {
                        subscriberTags = sampleAudience.segments.map((segment: any, idx: number) => (
                            <a
                                key={`i-${idx}`}
                                onClick={this.jumpToSegment.bind(this, segment)}
                                href={this.buildSegmentUrl(segment)}
                            >
                                <Tag color="gray">{segment.name}</Tag>
                            </a>
                        ))
                    }

                    subscribers = (
                        <div className="included-segments-header">
                            <span className="heading">Included Segments:</span>
                            <span className="segments">{subscriberTags}</span>
                        </div>
                    )
                } else if (sampleAudience.externalSubscriberIds) {
                    subscribers = <Tag color="gray">Subscriber IDs</Tag>
                }

                if (sampleAudience.excludedSegments && sampleAudience.excludedSegments.length > 0) {
                    excludedSubscribers = (
                        <div className="excluded-segments-header">
                            <span className="heading">Excluded Segments:</span>
                            <span className="segments">
                                {sampleAudience.excludedSegments.map((segment: any, idx: number) => (
                                    <a
                                        key={`x-${idx}`}
                                        onClick={this.jumpToSegment.bind(this, segment)}
                                        href={this.buildSegmentUrl(segment)}
                                    >
                                        <Tag color="gray">{segment.name}</Tag>
                                    </a>
                                ))}
                            </span>
                        </div>
                    )
                }
            }

            const optionsArray: any[] = []
            if (this.state.notification.defaultTemplate.isSilent) {
                optionsArray.push(
                    <span key="silent" className="option">
                        <Tooltip title="Silent">
                            <i className="icon-muted" />
                        </Tooltip>
                    </span>,
                )
            }
            if (this.state.notification.isTzDelivery()) {
                optionsArray.push(
                    <span key="stz" className="option">
                        <Tooltip title="Delivered in Subscriber's Time Zone">
                            <ClockCircleFilled />
                        </Tooltip>
                    </span>,
                )
            }
            if (optionsArray.length > 0) {
                optionsWrapper = (
                    <div className="options-header">
                        <span className="heading">Options:</span>
                        <span className="options">{optionsArray}</span>
                    </div>
                )
            }
        }

        return (
            <div className="notification-details page">
                {!this.state.loadingSchedule && (
                    <React.Fragment>
                        <NotificationDetailsHeader
                            domain={this.appState.currentDomain!}
                            notification={this.state.notification}
                        />

                        {this.renderTabs()}
                    </React.Fragment>
                )}
            </div>
        )
    }

    public renderSummaryTab(): React.ReactNode {
        const summaryNotification = extractSummaryNotification(this.state.notification!, this.state.platform)

        // default content
        let title = summaryNotification.title
        let body = summaryNotification.body
        let previewIcon = summaryNotification.icon
        let previewLandingUrl = summaryNotification.landingUrl
        let subtitle
        let actions = summaryNotification.actions

        if (['mac_os', 'windows', 'android'].includes(this.state.platform)) {
            title = this.state.notification?.webTemplate.title ?? summaryNotification.title
            previewLandingUrl = this.state.notification?.webTemplate.landingUrl ?? summaryNotification.landingUrl
            body = this.state.notification?.webTemplate.body ?? summaryNotification.body
            previewIcon = this.state.notification?.webTemplate.iconUrl ?? summaryNotification.icon
            actions = this.state.notification?.webTemplate.actions?.length
                ? this.state.notification?.webTemplate.actions
                : summaryNotification.actions
        }

        if (this.state.platform === 'ios') {
            title = this.state.notification?.nativeIosTemplate.title ?? summaryNotification.title
            previewLandingUrl = this.state.notification?.nativeIosTemplate.landingUrl ?? summaryNotification.landingUrl
            body = this.state.notification?.nativeIosTemplate.body ?? summaryNotification.body
            previewIcon = this.state.notification?.nativeIosTemplate.iconUrl ?? summaryNotification.icon
            subtitle = this.state.notification?.nativeIosTemplate.subtitle
        }

        if (this.state.platform === 'native_android') {
            title = this.state.notification?.nativeAndroidTemplate.title ?? summaryNotification.title
            previewLandingUrl =
                this.state.notification?.nativeAndroidTemplate.landingUrl ?? summaryNotification.landingUrl
            body = this.state.notification?.nativeAndroidTemplate.body ?? summaryNotification.body
            previewIcon = this.state.notification?.nativeAndroidTemplate.iconUrl ?? summaryNotification.icon
            actions = this.state.notification?.nativeAndroidTemplate.actions?.length
                ? this.state.notification?.nativeAndroidTemplate.actions
                : summaryNotification.actions
        }

        const previewTitle = <NoTranslate>{title}</NoTranslate>
        const previewBody = body ? <NoTranslate>{body}</NoTranslate> : null
        const previewSubtitle = subtitle ? <NoTranslate>{subtitle}</NoTranslate> : null

        const nativeFcmConfig = this.state.domain.nativeFcmConfiguration as any

        const notificationShouldUseImageForIconWhenPresent = nativeFcmConfig.useImageForIconWhenPresent

        const nps = this.notificationPreviewState.getCache('domain', this.state.domain.id)
        const initialPlatform = nps.resolvePlatformFromAvailableChannels(this.state.notification?.channels)
        const previewState = coalesce(nps.state, PreviewState.EXPANDED)

        return (
            <React.Fragment>
                <div className="outer-well-buttons">
                    <Button className="refresh-list" size="small" onClick={this.refreshStats}>
                        <RetweetOutlined />
                        <span>Refresh</span>
                    </Button>
                </div>

                {this.renderGenericStats()}
                <br />

                <Row gutter={24} className="details-lower-row">
                    <Col span={14}>{this.renderBreakdownStats()}</Col>
                    <Col span={10}>
                        <NotificationSummaryCard
                            mode="dark"
                            loading={this.state.loadingGenStats}
                            domain={this.state.domain}
                            notification={
                                {
                                    ...summaryNotification,
                                    title: previewTitle,
                                    subtitle: previewSubtitle,
                                    body: previewBody,
                                    actions: actions.map((action) => ({
                                        ...action,
                                        label: <NoTranslate key={action.id}>{action.label}</NoTranslate>,
                                    })),
                                    icon: previewIcon,
                                    accentColor: nativeFcmConfig.notificationAccentColor,
                                    useImageForIconWhenPresent: notificationShouldUseImageForIconWhenPresent,
                                    landingUrl: previewLandingUrl,
                                } as any
                            }
                            initialPlatform={initialPlatform}
                            previewState={previewState}
                            onPlatformChange={(platform) =>
                                this.setState({
                                    platform,
                                })
                            }
                            onTitleClick={this.handleSummaryTitleClick}
                            hideStats={true}
                            channels={this.state.notification?.channels}
                            hideFooter={true}
                            userId={this.appState.currentUser?.id}
                        />
                    </Col>
                </Row>
            </React.Fragment>
        )
    }

    private get notificationIdParam(): number {
        return this.injectedProps.match.params.notificationId
    }

    private get isDelivered(): boolean {
        const { notification } = this.state
        const completedStatuses = [StatusType.COMPLETED.name, StatusType.COMPLETED_WITH_FAILURES.name]
        return (
            !!notification &&
            (arrayContains(completedStatuses, notification.status) ||
                this.isPartialFailure ||
                notification.source === NotificationSource.CAMPAIGN)
        )
    }

    private get isPartialFailure(): boolean {
        const { notification, stats } = this.state
        const hasStats = !!stats && stats.impressions > 0
        return !!notification && notification.status === StatusType.FAILED.name && hasStats
    }

    private renderBreakdownStats(): React.ReactNode {
        const channelOptions = this.state.notification?.channels ?? []

        return (
            <Well
                className="thin primary-stats-well"
                hideHeader={true}
                hideFooter={true}
                loading={this.state.loadingBreakdownStats}
                disabled={this.isDisabled}
            >
                <div className="chart-header">
                    <div className="chart-title">Performance Breakdown by Channel</div>
                    <div className="chart-actions">
                        <Select
                            className="stats-type-select"
                            size="small"
                            value={this.state.statsType}
                            disabled={!this.state.breakdownStats?.length}
                            onChange={(val) => {
                                this.setState({ statsType: val })
                            }}
                        >
                            <Select.Option value={ReportDateIncrement.DAY}>Daily</Select.Option>
                            <Select.Option value={ReportDateIncrement.LIFETIME}>All Time</Select.Option>
                        </Select>
                        <div className="channel-selectors">
                            <DeliveryChannelSelector
                                disabled={!this.state.breakdownStats?.length}
                                disabledMessage="No stats are available for this Notification"
                                type="multiple"
                                nested={true}
                                onChange={(change: DeliveryChannel[]) => {
                                    this.setState({ performanceChannel: change })
                                }}
                                value={this.state.performanceChannel}
                                visibleChannels={channelOptions}
                                allowEmptySelection={true}
                            />
                        </div>
                    </div>
                </div>

                {!this.state.loadingBreakdownStats && this.renderBreakdownStatsChart()}
            </Well>
        )
    }

    private get isDisabled(): boolean {
        const isDelivered = this.isDelivered
        const isDelivering = this.state.notification!.status === StatusType.DELIVERING.name
        const isCancelled =
            this.state.notification!.status === StatusType.CANCELLED.name ||
            this.state.notification!.status === StatusType.CANCELLING.name
        const isInf = this.state.notification!.deliverySpec.window === NotificationDeliveryWindow.INFORMED
        const isStz = this.state.notification!.deliverySpec.window === NotificationDeliveryWindow.TIMEZONE

        return !isDelivered && !((isInf || isStz) && (isDelivering || isCancelled))
    }

    private renderBreakdownStatsChart = (): React.ReactNode => {
        const { stats, breakdownStats, notification } = this.state
        if (!stats) {
            return
        }
        if (!breakdownStats) {
            return ''
        }

        const xAxisDateDataSet = new Set(
            breakdownStats.map((stat) => moment.tz(stat.event_date, 'UTC').format('MMM D')),
        )

        const mapDataKeyFromObjectList = (key: string, objList: any[], channel: DeliveryChannel) => {
            return objList.filter((val) => val.channel === channel).map((filtered) => filtered[key] || 0)
        }

        const xAxisChannelDataSet = new Set(
            (notification?.channels ?? []).map((channel) => DeliveryChannel.getLongName(channel)),
        )

        const buildSeries = (chartType: ChartType) => {
            const series: any[] = [{ keys: ['name', 'y'], visible: false, showInLegend: false }]
            const colorType = chartType === 'impressions' ? 'primary' : 'secondary'

            if (this.state.statsType !== ReportDateIncrement.DAY) {
                series.push({
                    data: this.state.performanceChannel.map((channel) => {
                        const foundStat = breakdownStats.find((stat) => stat.channel === channel)
                        return {
                            name: DeliveryChannel.getLongName(channel),
                            color: HIGHCHARTS_CHANNEL_COLOR_MAP[channel][colorType],
                            y: foundStat?.[chartType] ?? 0,
                        }
                    }),
                })
            } else {
                series.push(
                    ...this.state.performanceChannel.map((channel) => ({
                        name: DeliveryChannel.getLongName(channel),
                        color: HIGHCHARTS_CHANNEL_COLOR_MAP[channel][colorType],
                        data: mapDataKeyFromObjectList(chartType, breakdownStats, channel),
                    })),
                )
            }

            return series
        }

        const options = (chartType: ChartType) => ({
            ...DEFAULT_HIGHCHARTS_CONFIG,
            swContext: (): IState => this.state,
            chart: {
                type: 'column',
                marginLeft: 70,
                marginRight: 50,
                spacingTop: 20,
            },
            title: {
                text: titleCase(chartType),
            },
            xAxis: {
                categories:
                    this.state.statsType === ReportDateIncrement.DAY ? [...xAxisDateDataSet] : [...xAxisChannelDataSet],
            },
            legend: {
                enabled: false,
            },
            yAxis: [
                {
                    title: { text: '' },
                    allowDecimals: false,
                    startOnTick: 0,
                    minRange: 0,
                },
            ],
            tooltip: {
                pointFormatter: HighchartsUtils.commaTooltipPointFormatter,
            },
            // plotOpt
            series: buildSeries(chartType),
        })

        let impressionsTotals = [
            <div key="impressions" className="ant-popover-title">
                Impressions:
            </div>,
        ]
        let clicksTotals = [
            <div key="clicks" className="ant-popover-title">
                Clicks:
            </div>,
        ]
        const totalStatsByChannel = reduceStatTotalsByChannel(breakdownStats)

        Object.keys(totalStatsByChannel).map((key) => {
            impressionsTotals.push(
                <div key={`${key}-impressions`} className="channel-totals-row">
                    <span className="channel-totals-label">{DeliveryChannel.getShortName(DeliveryChannel[key])}:</span>
                    <span className="channel-totals-value">
                        {numberWithCommas(totalStatsByChannel[key].impressions)}
                    </span>
                </div>,
            )

            clicksTotals.push(
                <div key={`${key}-clicks`} className="reach-breakdown-row">
                    <span className="reach-breakdown-label">{DeliveryChannel.getShortName(DeliveryChannel[key])}:</span>
                    <span className="reach-breakdown-value">{numberWithCommas(totalStatsByChannel[key].clicks)}</span>
                </div>,
            )
        })

        return (
            <Popover
                key={`popover-details`}
                overlayClassName="domain-notif-channel-select-details-popover"
                content={
                    <div>
                        <div className="impressions-breakdown-stats">{impressionsTotals}</div>

                        <div className="clicks-breakdown-stats">{clicksTotals}</div>
                    </div>
                }
                placement="left"
            >
                <HighchartsReact
                    className="impressions-chart"
                    highcharts={Highcharts}
                    options={options('impressions')}
                />

                <Divider type="horizontal" />

                <HighchartsReact highcharts={Highcharts} options={options('clicks')} />
            </Popover>
        )
    }

    private renderGenericStats(): React.ReactNode {
        const actions: any = this.state.notification?.template.channels.default?.actions ?? []
        const hasActions = actions.length > 0

        actions.sort((a, b) => (a.ordinal < b.ordinal ? 1 : a.ordinal < b.ordinal ? -1 : 0))

        const validPurchases = (this.state.stats?.purchases ?? 0) > 0
        const validPurchaseAmount = (this.state.stats?.purchase_amount ?? 0) > 0

        return (
            <Well
                className="thin gen-stats-well nested"
                loading={this.state.loadingGenStats}
                hideHeader={true}
                hideFooter={true}
            >
                <Well mode="ghost" hideHeader={true} hideFooter={true} disabled={this.isDisabled}>
                    <div className="chart-title">Impressions</div>
                    <div className="stat stat-impressions">{this.renderImpressions()}</div>
                </Well>
                <Well mode="ghost" hideHeader={true} hideFooter={true} disabled={this.isDisabled}>
                    <div className="chart-title">{hasActions && 'Total '}Clicks (CTR)</div>
                    <div className="stat stat-clicks">
                        {this.renderClicks()}
                        <div className="stat stat-ctr sub-stat">({this.renderCTR()})</div>
                    </div>
                </Well>

                {hasActions && (
                    <>
                        {this.renderPrimaryActionGenStats()}
                        {actions.map((action, idx) => (
                            <React.Fragment key={idx}>{this.renderActionGenStats(action, idx)}</React.Fragment>
                        ))}
                    </>
                )}

                {validPurchases && (
                    <Well mode="ghost" hideHeader={true} hideFooter={true} disabled={this.isDisabled}>
                        <div className="chart-title">Purchases</div>
                        <div className="stat stat-purchases">{this.renderPurchases()}</div>
                    </Well>
                )}

                {validPurchaseAmount && (
                    <Well mode="ghost" hideHeader={true} hideFooter={true} disabled={this.isDisabled}>
                        <div className="chart-title">Purchase Amount</div>
                        <div className="stat stat-purchase-amount">
                            {getDomainCurrencySymbol(this.appState.currentDomain)}
                            {this.renderPurchaseAmount()}
                        </div>
                    </Well>
                )}
            </Well>
        )
    }

    private renderPrimaryActionGenStats() {
        const stats: any = this.state.stats
        const actionStats: any = this.state.actionStats
        let primaryClicks: any
        let primaryCtr: any

        if (
            !this.isDisabled &&
            !this.state.loadingGenStats &&
            !this.state.loadingActionStats &&
            !!stats &&
            !!actionStats
        ) {
            primaryClicks = stats.primary_clicks
            primaryCtr = stats.impressions === 0 ? 0 : stats.primary_clicks / stats.impressions
        }

        return (
            <>
                <Well mode="ghost" hideHeader={true} hideFooter={true} disabled={this.isDisabled}>
                    <div className="chart-title">
                        <span>Primary Clicks</span>
                        <Tooltip title="Primary Landing URL">
                            <InfoCircleOutlined className="info-icon" />
                        </Tooltip>
                    </div>
                    <div className="stat stat-clicks">
                        {primaryClicks === undefined ? '--' : numberWithCommas(primaryClicks)}
                        <div className="stat stat-ctr sub-stat">
                            ({!primaryCtr ? '--' : numberWithPercent(primaryCtr)})
                        </div>
                    </div>
                </Well>
            </>
        )
    }

    private renderActionGenStats(action: any, actionIdx: number) {
        const stats: any = this.state.actionStats
        let clicks: any
        let ctr: any

        if (!this.isDisabled && !this.state.loadingActionStats && !!stats) {
            const stat = stats.find((s) => s.button.id.toString() === action.id.toString())
            if (!!stat) {
                clicks = stat.clicks
                ctr = stat.ctr_decimal
            }
        }

        return (
            <React.Fragment key={actionIdx}>
                <Well mode="ghost" hideHeader={true} hideFooter={true} disabled={this.isDisabled}>
                    <div className="chart-title">
                        <span>Button {actionIdx + 1} Clicks</span>
                        <Tooltip title={action.label}>
                            <InfoCircleOutlined className="info-icon" />
                        </Tooltip>
                    </div>
                    <div className="stat stat-clicks">
                        {clicks === undefined ? '--' : numberWithCommas(clicks)}
                        <div className="stat stat-ctr sub-stat">({!clicks ? '--' : numberWithPercent(ctr)})</div>
                    </div>
                </Well>
            </React.Fragment>
        )
    }

    private renderImpressions(): React.ReactNode {
        const stats: any = this.state.stats
        if (this.isDisabled || !stats) {
            return '--'
        }
        return numeral(stats.impressions).format('O,O')
    }

    private renderClicks(): React.ReactNode {
        const stats: any = this.state.stats
        if (this.isDisabled || !stats) {
            return '--'
        }
        return numeral(stats.clicks).format('O,O')
    }

    private renderCTR(): React.ReactNode {
        const stats: any = this.state.stats
        if (this.isDisabled || !stats) {
            return '--'
        }
        return numeral(stats.ctr_decimal).format('0.00%')
    }

    private renderPurchases(): React.ReactNode {
        const purchases = this.state.stats?.purchases

        return numeral(purchases).format('O,O')
    }

    private renderPurchaseAmount(): React.ReactNode {
        const purchaseAmount = this.state.stats?.purchase_amount

        return numeral(purchaseAmount).format('O,O')
    }

    private goBackToNotificationsList() {
        this.appService.routeWithinDomain('/notifications')
    }

    private async setDomainState(): Promise<void> {
        const { domain, notification } = this.state
        const selectedDomain = this.appState.currentDomain

        if (selectedDomain) {
            if (selectedDomain.id !== domain.id) {
                this.goBackToNotificationsList()
            } else {
                if (!this.unmounting) {
                    if (!domain) {
                        await this.setState({ domain: selectedDomain })
                    }

                    if (!notification) {
                        await this.ensureNotificationDetails(selectedDomain)
                    }
                }
            }
        }
    }

    private async ensureNotificationDetails(domain: DomainDto): Promise<void> {
        const notification = await this.notificationService.fetchNotificationById(domain.id, this.notificationIdParam, {
            query: { includeSegments: true },
            cancellationKey: 'nd.ensureNotificationDetails',
            version: ApiVersion.V4,
            errorHandler: onResponseError403(() => {
                this.goBackToNotificationsList()
            }),
        })

        const channelPlatform = {
            [DeliveryChannel.WEB]: PreviewPlatform.WINDOWS,
            [DeliveryChannel.NATIVE_IOS]: PreviewPlatform.IOS,
            [DeliveryChannel.NATIVE_ANDROID]: PreviewPlatform.ANDROID,
        }
        if (!this.unmounting && notification) {
            const channels = notification.channels

            await this.setState({
                loadingSchedule: false,
                notification,
                platform: channelPlatform[channels[0]],
            })

            this.getNotificationGenStats()
            this.getNotificationBreakdownStats()
        }
    }

    private async getNotificationGenStats(): Promise<void> {
        const { notification, domain } = this.state
        const actions = notification?.template.channels.default?.actions ?? []
        const hasActions = actions.length > 0

        if (!this.unmounting) {
            await this.setState({ loadingGenStats: true })

            const insightsCalls: Promise<any>[] = []
            insightsCalls.push(
                this.insightsService.fetch(
                    {
                        entity: ReportEntity.NOTIFICATIONS,
                        date_preset: ReportDatePreset.LIFETIME,
                        date_increment: ReportDateIncrement.LIFETIME,
                        breakdowns: ['notification'],
                        fields: [
                            'notification_id',
                            'deliveries',
                            'impressions',
                            'clicks',
                            'primary_clicks',
                            'delivery_rate_decimal',
                            'ctr_decimal',
                            'purchases',
                            'purchase_amount',
                        ],
                        filters: [
                            { field: 'domain.id', operator: 'eq', value: domain.id },
                            { field: 'notification.id', operator: 'eq', value: notification!.id },
                        ],
                    },
                    {
                        cancellationKey: 'notifDetails.genStats',
                    },
                ),
            )

            if (hasActions) {
                insightsCalls.push(
                    this.insightsService.fetch(
                        {
                            entity: ReportEntity.NOTIFICATIONS,
                            date_preset: ReportDatePreset.LIFETIME,
                            date_increment: ReportDateIncrement.LIFETIME,
                            action_attribution: 'event_time',
                            breakdowns: ['notification', 'button'],
                            fields: ['button.id', 'clicks', 'ctr_decimal'],
                            filters: [
                                { field: 'domain.id', operator: 'eq', value: domain.id },
                                { field: 'notification.id', operator: 'eq', value: notification!.id },
                            ],
                        },
                        {
                            cancellationKey: 'notifButtonDetails.bdStats',
                        },
                    ),
                )
            }

            const results = await Promise.all(insightsCalls)

            if (!this.unmounting) {
                this.setState({
                    loadingGenStats: false,
                    loadingActionStats: hasActions,
                    stats: results[0][0],
                })

                if (hasActions) {
                    this.setState({
                        loadingActionStats: false,
                        actionStats: results[1],
                    })
                } else {
                    this.setState({
                        loadingActionStats: false,
                    })
                }
            }
        }
    }

    private refreshStats = async (): Promise<void> => {
        this.setState({ loadingBreakdownStats: true, loadingGenStats: true })
        this.getNotificationGenStats()
        this.getNotificationBreakdownStats()
    }

    private async getNotificationBreakdownStats(): Promise<void> {
        const { notification, domain, statsType } = this.state
        await this.setState({ loadingBreakdownStats: true })
        const contract: InsightsReportContract = {
            entity: ReportEntity.NOTIFICATIONS,
            date_preset: ReportDatePreset.LIFETIME,
            date_increment: statsType,
            action_attribution: 'event_time',
            breakdowns: ['notification', 'channel'],
            fields: [
                'notification_id',
                'deliveries',
                'impressions',
                'clicks',
                'delivery_rate_decimal',
                'ctr_decimal',
                'channel',
            ],
            filters: [
                { field: 'domain.id', operator: 'eq', value: domain.id },
                { field: 'notification.id', operator: 'eq', value: notification!.id },
                { field: 'channel', operator: 'in', value: notification?.channels },
            ],
        }

        const breakdownStats = await this.insightsService.fetch(contract, {
            cancellationKey: 'notifDetails.bdStats',
        })

        if (breakdownStats.length === 0 || notification === undefined) {
            return this.setState({
                loadingBreakdownStats: false,
            })
        }

        const firstStatDate = moment.tz(breakdownStats[0].event_date, domain.timezone)
        const notifSendDate = moment.tz(moment.utc(notification!.deliverySpec.sendDateUtc), domain.timezone)
        // when notif send date is in STZ the first stats may be from the "previous" day due to tz diffs
        const startDate = firstStatDate.isBefore(notifSendDate) ? firstStatDate : notifSendDate

        let backfilledStats: any[]
        if (statsType === ReportDateIncrement.DAY) {
            backfilledStats = backfillStats(
                breakdownStats,
                statsType,
                {
                    clicks: 0,
                    impressions: 0,
                },
                notification?.channels,
                {
                    startDate,
                },
            )
        } else {
            backfilledStats = breakdownStats
        }

        this.setState({
            loadingBreakdownStats: false,
            breakdownStats: backfilledStats,
            performanceChannel: Array.from(new Set(notification?.channels ?? [])),
        })
    }

    private handleSummaryTitleClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
        this.stopPropagation(ev.nativeEvent)

        if (ev.metaKey) {
            window.open(this.buildScheduleDetailsUrl())
            return
        }

        this.appService.route(this.buildScheduleDetailsUrl())
    }

    private buildScheduleDetailsUrl(): string {
        const notification = this.state.notification
        if (!notification) {
            return this.appService.routeWithin('domain', `/notifications`, true)
        }

        return this.appService.routeWithin('domain', `/notifications/${notification.id}`, true)
    }

    private buildSegmentUrl(segment: any): string {
        return this.appService.routeWithinDomain(`/segments/${segment.id}/summary`, true)
    }

    private jumpToSegment = (segment: any, ev?: any): void => {
        if (!ev.metaKey) {
            this.stopPropagation(ev)
            this.appService.route(this.buildSegmentUrl(segment))
        }
    }

    private stopPropagation(ev: Event): void {
        ev.preventDefault()
        ev.stopPropagation()
    }
}
